﻿import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { getCityH1Toldos } from "@/config/city-h1-variants";
import { getCityFAQsToldos } from "@/config/city-types";
import { getCityTarifasToldos } from "@/config/city-pricing";
import Header from "@/components/feature/Header";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Footer from "@/components/feature/Footer";
import WhatsAppFloating from "@/components/feature/WhatsAppFloating";
import CookieBanner from "@/components/feature/CookieBanner";
import CrossLinksFooter from "@/components/feature/CrossLinksFooter";
import { NeighborCities } from "@/components/feature/NeighborCities";
import SEO, { generateFAQPageSchema } from "@/components/feature/SEO";
import { HeroFull } from "@/components/HeroFull";
import { HERO_IMAGE_DEFAULT } from "@/config/images";
import { getToldosProblemsImage, getProcessImage, getVideoCallImage, getTrustImage } from "@/config/city-images";

export interface CityPageToldosProps {
  name: string;
  slug: string;
  cp: string;
  poblacion: number;
  zonaGeo: string;
  lat: number;
  lng: number;
  zonas: string[];
  landmarks: string[];
  municipiosLimitrofes: string[];
  festivalLocal: string;
  contextoTipologico: string;
  isCoreZone: boolean;
}

export default function CityPageToldos({
  name,
  slug,
  cp,
  zonaGeo,
  lat,
  lng,
  zonas,
  landmarks,
  festivalLocal,
  contextoTipologico,
  municipiosLimitrofes,
  isCoreZone,
}: CityPageToldosProps) {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);
  const handleSubmit = useFormSubmit(agreed);

  const tarifas = getCityTarifasToldos(slug, name);

  const altTexts = [
    `Técnico instalando toldo motorizado en terraza de ${name}`,
    `Toldo extensible nuevo en ático del centro de ${name}`,
    `Toldo cofre instalado en vivienda de ${zonas?.[0] || name}`,
    `Pérgola bioclimática en chalet de ${zonas?.[1] || name}`,
    `Reparación de toldo desgastado por salitre en ${name}`,
  ];

  const intro = `En ${name}, los toldos se enfrentan a condiciones específicas. ${contextoTipologico || 'El clima mediterráneo de la Costa del Sol exige lonas resistentes al salitre marino, al viento de levante y a la radiación solar intensa de más de 300 días al año.'} ${landmarks?.length ? `Instalamos regularmente en viviendas y locales cerca de ${landmarks.slice(0,2).join(' y ')}, ` : ''}desde áticos modernos en ${zonas?.[0] || 'el centro'} hasta chalets independientes en ${zonas?.[zonas.length-1] || 'las urbanizaciones'}. ${festivalLocal ? `Durante eventos como ${festivalLocal}, la demanda de instalaciones y reparaciones aumenta significativamente, por eso garantizamos visita en 24h incluso en temporada alta. ` : ''}Cubrimos todo ${name} con técnico propio, sin intermediarios, ofreciendo presupuesto gratuito, visita en 24 horas y garantía de 5 años en motor y 3 en lona.`;

  const problemas = [
    { emoji: "☀️", text: "Sombra en mi terraza/balcón", link: true },
    { emoji: "🏠", text: "Cubrir patio o porche", link: true },
    { emoji: "⚡", text: "Toldo motorizado con mando", link: true },
    { emoji: "🔧", text: "Reparar mi toldo existente", link: true },
    { emoji: "🌬️", text: "Toldo resistente al viento", link: true },
    { emoji: "🛡️", text: "Lona protegida (toldo cofre)", link: true },
  ];

  const pasos = [
    { icon: "📞", title: "Contacto rápido", desc: "Llamada o WhatsApp en menos de 20 min" },
    { icon: "📹", title: "Diagnóstico", desc: "Videollamada o visita técnica gratis" },
    { icon: "📐", title: "Medición y diseño 3D", desc: "Sin compromiso de compra" },
    { icon: "🔨", title: "Fabricación + instalación", desc: "15-20 días desde pedido" },
    { icon: "✅", title: "Garantía 5 años motor", desc: "Certificado por escrito" },
  ];

  const trustItems = [
    { emoji: "🛡️", text: "Seguro Responsabilidad Civil" },
    { emoji: "🕐", text: "+10 años · desde 2015" },
    { emoji: "🏆", text: "Marcas premium: Somfy, Markilux, Tempotest" },
    { emoji: "📝", text: "Garantía 5 años motor, 3 años lona" },
  ];

  const beneficiosToldo = [
    { emoji: "☀️", text: `+300 días de sol/año en ${name}` },
    { emoji: "🌡️", text: "Reduce hasta 8°C la temperatura interior" },
    { emoji: "💡", text: "Ahorra hasta 30% en aire acondicionado" },
  ];

  const faqs = getCityFAQsToldos(slug, name);

  const faqSchema = generateFAQPageSchema(faqs);

  const handleFormSubmit = (e: React.FormEvent) => {
    handleSubmit(e, formRef);
    if (agreed) setFormSubmitted(true);
  };

  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const waVideoLink = `https://wa.me/34627213347?text=Hola%2C%20quiero%20un%20videodiagn%C3%B3stico%20de%20toldo`;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Toldos ${name} | Instalación y Reparación con Garantía`}
        description={`Toldos en ${name}: motorizados, cofre, extensibles. Diagnóstico WhatsApp gratis. Garantía 5 años. ☎ 951 12 07 70`}
        canonical={`/toldos/${slug}/`}
        ogImage={`https://persianas-toldos-malaga.es/og/og-toldos-${slug}.jpg`}
        ogType="website"
        breadcrumbs={[
          { name: "Inicio", path: "/" },
          { name: "Toldos", path: "/toldos" },
          { name },
        ]}
      />
      <Header />
      <main>
        <Breadcrumbs items={[{ name: "Inicio", path: "/" }, { name: "Toldos", path: "/toldos" }, { name }]} />
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt={`Instalación y reparación de toldos en ${name}`}
          badge="⚡ Presupuesto GRATIS en 24h"
          title={getCityH1Toldos(slug, name)}
          subtitle="Toldos motorizados, cofre, brazos invisibles, pérgolas. Garantía 5 años en motor."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "https://wa.me/34627213347", label: "💬 WhatsApp" }}
          trustSignals={["Técnico propio", "Garantía 5 años motor", "Presupuesto gratis"]}
        />

        {/* ===== BLOQUE 1.5 — CONTEXTO LOCAL ===== */}
        <section className="bg-white py-12 md:py-16 border-t border-border">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              {/* Left: text */}
              <div className="w-full lg:w-1/2">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-text-primary mb-4">
                  Instalación de toldos adaptada a {name}{zonas?.length >= 3 ? ` y barrios como ${zonas.slice(0,3).join(', ')}` : ''}
                </h2>
                <p className="font-body text-base md:text-lg text-text-secondary leading-relaxed">
                  {intro}
                </p>
              </div>
              {/* Right: image */}
              <motion.div
                className="w-full lg:w-1/2"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="relative rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20professional%20technician%20installing%20a%20modern%20motorized%20retractable%20canvas%20awning%20on%20a%20beautiful%20Mediterranean%20terrace%20with%20white%20exterior%20walls%2C%20sunny%20day%20with%20blue%20sky%2C%20clean%20professional%20service%20photography%2C%20warm%20afternoon%20light&width=800&height=500&seq=ctx-toldos-city&orientation=landscape&format=webp"
                    alt={`Instalación de toldos en ${name} por técnico profesional`}
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* BLOQUE 2 — ¿BUSCAS ESTO? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Buscas esto en {name}?</h2>
            <div className="w-10 h-1 bg-primary rounded-full mb-10"></div>
            <div className="max-w-2xl mx-auto mb-10">
              <img
                src={getToldosProblemsImage()}
                alt={altTexts[1]}
                className="w-full h-auto rounded-xl object-cover aspect-[16/9]"
                loading="lazy"
                width="600"
                height="338"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {problemas.map((p, i) => (
                <motion.button
                  key={p.text}
                  onClick={scrollToForm}
                  className="bg-white rounded-2xl p-6 text-left shadow-sm hover:shadow-md transition-all border border-border hover:border-primary group cursor-pointer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <div className="text-3xl mb-3">{p.emoji}</div>
                  <h3 className="font-body font-semibold text-base text-text-primary mb-2">{p.text}</h3>
                  <span className="inline-flex items-center gap-1 text-sm font-body font-medium text-primary group-hover:underline">
                    Sí, esto me interesa <span>→</span>
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN A — 2 COL: PROCESO (imagen IZQ, texto DER) ===== */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Imagen */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={getProcessImage("toldos")}
                  alt={altTexts[2]}
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
              {/* COLUMNA DERECHA — Texto */}
              <motion.div
                className="flex flex-col gap-6"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase text-primary">NUESTRO PROCESO</p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Así te ayudamos en {name}
                </h2>
                <ol className="flex flex-col gap-5">
                  {pasos.map((s) => (
                    <li key={s.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 shrink-0 bg-accent/40 rounded-full flex items-center justify-center text-xl" aria-hidden="true">
                        {s.icon}
                      </div>
                      <div>
                        <p className="font-heading font-bold text-base text-text-primary">{s.title}</p>
                        <p className="font-body text-sm text-text-secondary leading-relaxed">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <div>
                  <button
                    onClick={scrollToForm}
                    className="inline-flex items-center gap-2 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-body font-bold text-base px-6 py-3 rounded-pill transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Pedir presupuesto <span aria-hidden="true">→</span>
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* BLOQUE 4 — TARIFAS */}
        <section id="tarifas" className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Tarifas indicativas de toldos en {name}</h2>
            <div className="w-10 h-1 bg-primary rounded-full mb-8"></div>
            <div className="max-w-2xl">
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-border">
                {tarifas.map((t, i) => (
                  <div key={t.tipo} className={`flex items-center justify-between px-6 py-4 ${i < tarifas.length - 1 ? "border-b border-border" : ""} ${t.precio === "GRATIS" ? "bg-accent/30" : ""}`}>
                    <span className="font-body text-sm md:text-base text-text-primary">{t.tipo}</span>
                    <span className={`font-heading font-bold text-base md:text-lg ${t.precio === "GRATIS" ? "text-primary" : "text-text-primary"}`}>{t.precio}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 font-body text-xs text-text-secondary">
                Tarifas orientativas en {name}. Presupuesto detallado tras visita gratuita. Precios finales pueden variar según medidas, acceso y materiales específicos.
              </p>
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN B — 2 COL: VIDEODIAGNÓSTICO (texto IZQ, imagen DER, fondo amarillo) ===== */}
        <section className="py-16 md:py-24" style={{ backgroundColor: "#ECFCCB" }}>
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Texto */}
              <motion.div
                className="flex flex-col gap-6 md:order-1"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase" style={{ color: "#D97706" }}>
                  📹 NOVEDAD
                </p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Diagnóstico GRATIS por videollamada WhatsApp
                </h2>
                <p className="font-body text-lg text-text-secondary leading-relaxed">
                  ¿No estás seguro del problema? Muéstranos por videollamada y te asesoramos <strong>GRATIS</strong>. Sin compromiso, sin desplazamiento, en menos de 20 minutos.
                </p>
                <ul className="flex flex-col gap-3">
                  {["Diagnóstico instantáneo", "Presupuesto orientativo al momento", "Ahorra la visita técnica si es sencillo", "Disponible L-V 9-19h, S 9-14h"].map((b) => (
                    <li key={b} className="flex items-center gap-3 font-body text-sm text-text-primary">
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs" aria-hidden="true">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20quiero%20un%20videodiagn%C3%B3stico"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-xl px-8 py-5 rounded-pill transition-colors whitespace-nowrap w-fit"
                >
                  <span aria-hidden="true">📹</span> Iniciar videollamada
                </a>
              </motion.div>
              {/* COLUMNA DERECHA — Imagen */}
              <motion.div
                className="md:order-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <img
                  src={getVideoCallImage()}
                  alt={`Diagnóstico videollamada WhatsApp toldos ${name}`}
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN C — 2 COL: POR QUÉ NOSOTROS (imagen IZQ, texto DER) ===== */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Imagen */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={getTrustImage()}
                  alt={altTexts[4]}
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
              {/* COLUMNA DERECHA — Texto */}
              <motion.div
                className="flex flex-col gap-6"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase text-primary">POR QUÉ NOSOTROS</p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Empresa local desde 2015 · +10 años de experiencia
                </h2>
                <p className="font-body text-lg text-text-secondary leading-relaxed">
                  No somos plataforma intermediaria. Hablas directamente con quien va a instalar tu toldo en {name}. Sin sorpresas, sin sobreprecios.
                </p>
                <ul className="flex flex-col gap-4">
                  {trustItems.map((t) => (
                    <li key={t.text} className="flex items-start gap-4">
                      <span className="text-2xl shrink-0" aria-hidden="true">{t.emoji}</span>
                      <p className="font-heading font-bold text-sm text-text-primary">{t.text}</p>
                    </li>
                  ))}
                </ul>
                <div>
                  <Link
                    to="/sobre-nosotros"
                    className="inline-flex items-center gap-2 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-body font-bold text-base px-6 py-3 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Conócenos <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* BLOQUE 8 — ¿POR QUÉ UN TOLDO? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-4">¿Por qué un toldo en {name}?</h2>
            <div className="w-10 h-1 bg-primary rounded-full mx-auto mb-10"></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
              {beneficiosToldo.map((b, i) => (
                <motion.div
                  key={b.text}
                  className="bg-white rounded-2xl p-6 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                >
                  <div className="text-3xl mb-3">{b.emoji}</div>
                  <p className="font-body font-semibold text-sm text-text-primary">{b.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* BLOQUE 9 — COBERTURA */}
        <section className="bg-surface-alt py-12 md:py-16">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <p className="font-body text-base text-text-secondary leading-relaxed">
              Trabajamos en todos los barrios de {name} y en {municipiosLimitrofes.slice(0, 3).join(", ")}. Sin coste de desplazamiento.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {zonas.map((z) => (
                <span key={z} className="inline-flex items-center bg-white border border-border rounded-pill px-3 py-1 font-body text-xs text-text-secondary">
                  {z}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* BLOQUE 10 — FAQ */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-4">Preguntas frecuentes sobre toldos en {name}</h2>
            <div className="w-10 h-1 bg-primary rounded-full mx-auto mb-10"></div>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((f, i) => (
                <motion.div
                  key={i}
                  className="bg-surface-alt rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{f.q}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{f.a}</p>
                </motion.div>
              ))}
            </div>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
          </div>
        </section>

        <NeighborCities slug={slug} service="toldos" />

        {/* BLOQUE 11 — CTA FINAL + FORM */}
        <section ref={formSectionRef} id="presupuesto" className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              <div className="w-full lg:w-[40%]">
                <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                  <h2 className="font-heading font-extrabold text-3xl md:text-[40px] text-text-primary mb-4 leading-tight">
                    Pídenos presupuesto para toldos en {name}
                  </h2>
                  <p className="font-body text-lg text-text-secondary mb-8">
                    Te respondemos en menos de 20 min. Visita técnica y diseño 3D gratuitos en {name}.
                  </p>
                  <div className="flex flex-col gap-4 mb-8">
                    <a href="tel:+34951120770" className="inline-flex items-center gap-3 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors w-fit whitespace-nowrap">
                      <span>📞</span> Llamar: 951 12 07 70
                    </a>
                    <a href={waVideoLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-lg px-6 py-4 rounded-pill transition-colors w-fit whitespace-nowrap">
                      <span>📹</span> Diagnóstico WhatsApp
                    </a>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-heading font-bold text-base text-text-primary">También en {name}:</h4>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/persianas/${slug}/`} className="inline-flex items-center gap-1.5 bg-white border border-border hover:border-primary text-text-primary hover:text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill transition-colors">
                        🔧 Persianas
                      </Link>
                      <Link to={`/mosquiteras/${slug}/`} className="inline-flex items-center gap-1.5 bg-white border border-border hover:border-primary text-text-primary hover:text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill transition-colors">
                        🦟 Mosquiteras
                      </Link>
                    </div>
                  </div>
                  {isCoreZone && (
                    <div className="mt-6">
                      <Link to={`/zonas/${slug}`} className="inline-flex items-center gap-2 bg-white border border-border hover:border-primary text-text-primary hover:text-primary font-body font-semibold text-sm px-5 py-3 rounded-pill transition-colors">
                        Ver todos los servicios en {name} <span>→</span>
                      </Link>
                    </div>
                  )}
                </motion.div>
              </div>
              <motion.div className="w-full lg:w-[60%]" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
                <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm">
                  {!formSubmitted ? (
                    <form ref={formRef} action="https://formspree.io/f/mdajpjzd" method="POST" encType="application/x-www-form-urlencoded" data-readdy-form onSubmit={handleFormSubmit} className="flex flex-col gap-5">
                      <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                      <input type="hidden" name="ciudad" value={name} />
                      <input type="hidden" name="servicio" value="Toldos" />
                      <div>
                        <label htmlFor={`nombre-toldos-${slug}`} className="block font-body font-medium text-sm text-text-primary mb-1.5">Nombre <span className="text-primary">*</span></label>
                        <input id={`nombre-toldos-${slug}`} name="nombre" type="text" required className="w-full h-12 px-4 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" placeholder="Tu nombre" />
                      </div>
                      <div>
                        <label htmlFor={`telefono-toldos-${slug}`} className="block font-body font-medium text-sm text-text-primary mb-1.5">Teléfono <span className="text-primary">*</span></label>
                        <div className="flex">
                          <span className="inline-flex items-center px-4 border border-r-0 border-border rounded-l-xl bg-slate-50 font-body text-sm text-text-secondary shrink-0">+34</span>
                          <input id={`telefono-toldos-${slug}`} name="telefono" type="tel" required className="w-full h-12 px-4 border border-border rounded-r-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" placeholder="612 34 56 78" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor={`mensaje-toldos-${slug}`} className="block font-body font-medium text-sm text-text-primary mb-1.5">Mensaje <span className="text-text-secondary">(opcional)</span></label>
                        <textarea id={`mensaje-toldos-${slug}`} name="mensaje" rows={3} maxLength={500} className="w-full px-4 py-3 border border-border rounded-xl font-body text-sm text-text-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all resize-none" placeholder={`Cuéntanos qué tipo de toldo necesitas en ${name}...`}></textarea>
                        <p className="mt-1 text-xs text-text-secondary font-body">Máximo 500 caracteres</p>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <input id={`privacidad-toldos-${slug}`} name="privacidad" type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 w-4 h-4 accent-primary shrink-0" />
                        <label htmlFor={`privacidad-toldos-${slug}`} className="font-body text-sm text-text-secondary leading-relaxed">He leído y acepto la <Link to="/politica-privacidad" className="underline text-primary hover:text-primary-dark transition-colors">Política de Privacidad</Link> <span className="text-primary">*</span></label>
                        <p className="font-body text-xs text-text-secondary/70 mt-1 leading-relaxed">
                          Responsable: Persianas Toldos Málaga. Finalidad: responder a tu consulta y enviarte presupuesto. Legitimación: tu consentimiento. Destinatarios: no se cederán datos a terceros. Derechos: puedes ejercer tus derechos de acceso, rectificación y supresión escribiendo a info@persianas-toldos-malaga.es
                        </p>
                      </div>
                      <button type="submit" className="w-full h-14 bg-text-primary hover:bg-slate-800 text-white font-heading font-bold text-lg rounded-pill transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
                        Pedir presupuesto gratis <span>→</span>
                      </button>
                      <p className="text-center font-body text-sm text-text-secondary">o llámanos al <a href="tel:+34951120770" className="font-semibold text-primary hover:text-primary-dark underline">951 12 07 70</a></p>
                    </form>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-5xl mb-4">🎉</div>
                      <h3 className="font-heading font-bold text-2xl text-text-primary mb-3">¡Presupuesto solicitado!</h3>
                      <p className="font-body text-base text-text-secondary mb-6">Te contactamos en menos de 20 min. Mientras tanto, puedes llamarnos al <a href="tel:+34951120770" className="font-semibold text-primary underline">951 12 07 70</a>.</p>
                      <Link to="/" className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold px-6 py-3 rounded-pill transition-colors">
                        Volver al inicio
                      </Link>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <CrossLinksFooter serviceSlug="toldos" cityName={name} currentSlug={slug} />
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}